/**
 * An interface for sending events to GA4.
 *
 * Note that all events must be added to the GTM console.
 * Sending an event with an unknown name produces no results,
 * no requests, hard to debug.
 *
 * Event names are defined here:
 * https://tagmanager.google.com/#/container/accounts/6000141753/containers/30150878/workspaces/20/tags
 *
 * Parameter names are also important, defined here:
 * https://tagmanager.google.com/#/container/accounts/6000141753/containers/30150878/workspaces/20/variables
 */
import { isCookieDeclined } from "@/common/utils/cookie";

interface eventData {
  [key: string]: string | string[] | number | boolean | null | undefined;
}

declare global {
  interface Window {
    dataLayer: eventData[];
  }
}

class GaMetrics {
  async sendAddedToCollection(): Promise<void> {
    await this.sendRawEvent({
      event: "addedToCollection",
    });
  }

  /**
   * Send when the ad script loaded successfully.
   * Not for tracking individual ads.
   */
  async sendAdLoaded(): Promise<void> {
    await this.sendRawEvent({
      event: "AdLoaded",
    });
  }

  async sendFollowUserClicked(): Promise<void> {
    await this.sendRawEvent({
      event: "followClicked",
    });
  }

  async sendGifClick(): Promise<void> {
    await this.sendRawEvent({
      event: "gifClick",
    });
  }

  async sendGifView(): Promise<void> {
    await this.sendRawEvent({
      event: "gifView",
    });
  }

  async sendMeaningfulGifView(): Promise<void> {
    await this.sendRawEvent({
      event: "meaningfulGifView",
    });
  }

  async sendGifReplay(): Promise<void> {
    await this.sendRawEvent({
      event: "gifReplay",
    });
  }

  async sendLiked(): Promise<void> {
    await this.sendRawEvent({
      event: "liked",
    });
  }

  async sendMenuButtonClicked(): Promise<void> {
    await this.sendRawEvent({
      event: "menuButtonClicked",
    });
  }

  async sendProfileView(): Promise<void> {
    await this.sendRawEvent({
      event: "profileView",
    });
  }

  async sendSearch(): Promise<void> {
    await this.sendRawEvent({
      event: "search",
    });
  }

  async sendSignUpFormView(): Promise<void> {
    await this.sendRawEvent({
      event: "signUpView",
    });
  }

  async sendSignUpSuccessful(): Promise<void> {
    await this.sendRawEvent({
      event: "signUpSuccess",
    });
  }

  async sendSocialProfileClick(): Promise<void> {
    await this.sendRawEvent({
      event: "socialProfileClicked",
    });
  }

  async sendTagClicked(): Promise<void> {
    await this.sendRawEvent({
      event: "tagClicked",
    });
  }

  async sendUploadFinisher(): Promise<void> {
    await this.sendRawEvent({
      event: "uploadFinished",
    });
  }

  async sendUploadStarted(): Promise<void> {
    await this.sendRawEvent({
      event: "uploadStarted",
    });
  }

  async sendUploadUrlSelected(): Promise<void> {
    await this.sendRawEvent({
      event: "uploadUrlSelected"
    });
  }

  async sendUploadImageSelected(): Promise<void> {
    await this.sendRawEvent({
      event: "uploadImageSelected"
    });
  }

  async sendUploadImageConfirmed(): Promise<void> {
    await this.sendRawEvent({
      event: "uploadImageConfirmed"
    });
  }

  async sendUploadVideoSelected(): Promise<void> {
    await this.sendRawEvent({
      event: "uploadVideoSelected"
    });
  }

  async sendUploadVideoTrim(): Promise<void> {
    await this.sendRawEvent({
      event: "uploadVideoTrim"
    });
  }

  async sendUploadVideoConfirmed(): Promise<void> {
    await this.sendRawEvent({
      event: "uploadVideoConfirmed"
    });
  }

  async sendUploadAudienceDefined(): Promise<void> {
    await this.sendRawEvent({
      event: "uploadAudienceDefined"
    });
  }

  async sendUploadTagsSelected(): Promise<void> {
    await this.sendRawEvent({
      event: "uploadTagsSelected"
    });
  }

  async sendUploadGetVerifiedClicked(): Promise<void> {
    await this.sendRawEvent({
      event: "uploadGetVerifiedClicked"
    });
  }

  async sendUploadPublished(): Promise<void> {
    await this.sendRawEvent({
      event: "uploadPublished"
    });
  }

  async sendUploadMultipleConfirmed(): Promise<void> {
    await this.sendRawEvent({
      event: "uploadMultipleConfirmed"
    });
  }

  async sendUploadVideoDropped(): Promise<void> {
    await this.sendRawEvent({
      event: "uploadVideoDropped"
    });
  }

  async sendUploadImageDropped(): Promise<void> {
    await this.sendRawEvent({
      event: "uploadImageDropped"
    });
  }

  async sendUploadImagesDropped(): Promise<void> {
    await this.sendRawEvent({
      event: "uploadImagesDropped"
    });
  }

  async sendOptionClick(): Promise<void> {
    await this.sendRawEvent({
      event: "optionClick"
    });
  }

  async sendShareClick(): Promise<void> {
    await this.sendRawEvent({
      event: "shareClick"
    });
  }

  async sendShareCopy(): Promise<void> {
    await this.sendRawEvent({
      event: "shareCopy"
    });
  }

  async sendShareEmbed(): Promise<void> {
    await this.sendRawEvent({
      event: "shareEmbed"
    });
  }

  async sendShareReddit(): Promise<void> {
    await this.sendRawEvent({
      event: "shareReddit"
    });
  }

  async sendShareTwitter(): Promise<void> {
    await this.sendRawEvent({
      event: "shareTwitter"
    });
  }

  async sendShareDiscord(): Promise<void> {
    await this.sendRawEvent({
      event: "shareDiscord"
    });
  }

  async sendShareEmbedFixed(): Promise<void> {
    await this.sendRawEvent({
      event: "shareEmbedFixed"
    });
  }

  async sendShareEmbedResponsive(): Promise<void> {
    await this.sendRawEvent({
      event: "shareEmbedResponsive"
    });
  }

  async sendEnterFullscreen(): Promise<void> {
    await this.sendRawEvent({
      event: "enterFullscreen"
    });
  }

  async sendScrollPromptShow(): Promise<void> {
    await this.sendRawEvent({
      event: "scrollPromptShow"
    });
  }

  async sendScrollPromptDismiss(): Promise<void> {
    await this.sendRawEvent({
      event: "scrollPromptDismiss"
    });
  }

  async sendUploadBulkClick(): Promise<void> {
    await this.sendRawEvent({
      event: "uploadBulkClick"
    });
  }

  async sendSessionStartPrivate(): Promise<void> {
    await this.sendRawEvent({
      event: "sessionStartPrivate"
    });
  }

  async sendInfobarCta(): Promise<void> {
    await this.sendRawEvent({
      event: "infobarСta"
    });
  }

  async sendInfobarDismiss(): Promise<void> {
    await this.sendRawEvent({
      event: "infobarDismiss"
    });
  }

  async sendPinnedContentClick(): Promise<void> {
    await this.sendRawEvent({
      event: "pinnedContentClick"
    });
  }

  async sendWatch120s(): Promise<void> {
    await this.sendRawEvent({
      event: "watched120s"
    });
  }

  async sendVoteUpAddClick(): Promise<void> {
    await this.sendRawEvent({
      event: "voteUpAddClick"
    });
  }

  async sendVoteUpRemoveClick(): Promise<void> {
    await this.sendRawEvent({
      event: "voteUpRemoveClick"
    });
  }

  async sendVoteDownAddClick(): Promise<void> {
    await this.sendRawEvent({
      event: "voteDownAddClick"
    });
  }

  async sendVoteDownRemoveClick(): Promise<void> {
    await this.sendRawEvent({
      event: "voteDownRemoveClick"
    });
  }

  async sendErrorLoadingGifs(): Promise<void> {
    await this.sendRawEvent({
      event: "sendErrorLoadingGifs"
    });
  }

  async sendTagFeedbackEnabled(): Promise<void> {
    await this.sendRawEvent({
      event: "tagFeedbackEnabled"
    });
  }

  async sendTagFeedbackEnabledOptions(): Promise<void> {
    await this.sendRawEvent({
      event: "tagFeedbackEnabledOptions"
    });
  }

  async sendPlayerNicheClick(): Promise<void> {
    await this.sendRawEvent({
      event: "playerNicheClick",
    });
  }

  async sendPlayerTagClick(): Promise<void> {
    await this.sendRawEvent({
      event: "playerTagClick",
    });
  }

  async sendVerificationSubmit(): Promise<void> {
    await this.sendRawEvent({
      event: "verificationSubmit",
    });
  }

  async sendHorizontalOrientation(): Promise<void> {
    await this.sendRawEvent({
      event: "orientationChangeH",
    });
  }

  async sendVerticalOrientation(): Promise<void> {
    await this.sendRawEvent({
      event: "orientationChangeV",
    });
  }

  async sendViewModeFeedChanged(): Promise<void> {
    await this.sendRawEvent({
      event: "viewModeFeedChanged",
    });
  }

  async sendPWASessionStarted(): Promise<void> {
    await this.sendRawEvent({
      event: "pwaSessionStart"
    });
  }

  async sendNicheGifsOrderChanged(): Promise<void> {
    await this.sendRawEvent({
      event: "nicheGifsOrderChanged",
    });
  }

  async sendNicheCategoryClicked(): Promise<void> {
    await this.sendRawEvent({
      event: "nicheCategoryClicked",
    });
  }

  async sendRawEvent(event: eventData) {
    if (await isCookieDeclined(["analytics"])) {
      return;
    }

    window.dataLayer = window.dataLayer ? window.dataLayer : [];

    const res = window.dataLayer.push(event);

    // @ts-expect-error https://tagmanager.google.com/#/container/accounts/6000141753/containers/30150878/workspaces/20/tags
    if (res === true) {
      console.warn(`[GTM] Event not set, make sure that "${event.event}" is added properly`
        + " in the console here: https://tagmanager.google.com/#/container/accounts/6000141753/containers/30150878/workspaces/20/tags", event);
    } else {
      console.debug("[GTM] Event sent.", event);
    }
  }
}

export default new GaMetrics();
